import Vue from "vue";
import store from "@/store";
import router from "@/router";
import { v4 as uuidv4 } from "uuid";
import { MUTATION_NAME } from "@/static/global-variables";
import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
import * as ProxyElements from "@/views/drag-drop-pages/components/utils/component-draggable-callbacks";
import { LAYOUT_TYPE } from "@/views/drag-drop-pages/static/layout-type";
import i18n from "@/plugins/i18n";

/**
 * Helper object to create draggable objects
 */
class DragAndDrop {
  constructor() {
    // possible drop effects
    this.DROP_EFFECT = Object.freeze({
      COPY: "copy",
      MOVE: "move",
      LINK: "link",
      NONE: "none"
    });
    // to indicate what is allowed when dropping the draggable element
    this.EFFECT_ALLOWED = Object.freeze({
      NONE: "none",
      COPY: "copy",
      COPY_LINK: "copyLink",
      COPY_MOVE: "copyMove",
      MOVE: "move",
      ALL: "all",
      UNINITIALIZED: "uninitialized"
    });

    // components that can be created - modals or components
    this.components = {};

    // exising appID
    this.appId;
    // Object with info about which element will be creat
    this.createElementData;
    // to keep track of which components have canvas to drop elements and do cleanup
    this.canvasElementList = {};

    store.subscribe(mutation => {
      if (mutation.type === MUTATION_NAME.SET_DELETE_DRAGGABLE) {
        this.deleteComponent(mutation.payload);
      } else if (mutation.type === MUTATION_NAME.SET_EDIT_DRAGGABLE) {
        this.onElementEdit(mutation.payload);
      }
    });
  }
  setAppId(appId) {
    this.appId = appId;
  }
  getAppId() {
    return this.appId;
  }

  updateIsDirtyFlag(data) {
    data.appId = this.appId;
    store.commit(MUTATION_NAME.UPDATE_IS_DIRTY_FLAG, data);
  }

  /**
   * Will call onElementEdit for each registered component.
   * The component will determine if it has the element to be edited.
   * @param {Object} ev - Event with info about component
   */
  onElementEdit(ev) {
    for (const [, value] of Object.entries(this.canvasElementList)) {
      value.$el.draggable = false;
      if (value.onElementEdit) {
        value.onElementEdit(ev.detail);
      }
    }
  }

  makeAllComponentsDraggable() {
    for (const [key, value] of Object.entries(this.canvasElementList)) {
      if (key !== "Main Page") {
        value.$el.draggable = true;
      }
    }
  }

  /**
   * Sets object with components that can be created - modals and components
   * @param {Object} components
   */
  setComponets(components) {
    this.components = components;
  }

  /**
   * Function to create a modal
   * @param {String} modal - Name of modal
   * @param {Function} dismissModal - Function callback when user closes or cancels the modal
   * @param {Function} onModalDone - Function callback when user clicks on done button
   * @param {Object} editData - Optional object with info about existing component
   */
  createModal(modal, dismissModal, onModalDone, editData) {
    const from = this.components[modal];
    if (from) {
      const instance = this.getModalInstance(
        from,
        dismissModal,
        onModalDone,
        editData
      );
      return instance;
    }
  }

  /**
   * Creates an instance of draggable component
   * @param {String} create - name of component
   * @param {Object} data - Object with info props to create the component
   * @param {Boolean} isDraggable - optional
   * @param {Function} onComponentStartDrag - optional callbacks outside its own container. E.g. a canvas layout
   * @param {Function} onComponentEndDrag - optional callbacks outside its own container. E.g. a canvas layout
   */
  createDraggableComponent(
    create,
    data,
    isDraggable,
    onComponentStartDrag,
    onComponentEndDrag
  ) {
    const from = this.components[create];
    if (from) {
      const instance = this.buildDraggableInstance(
        from,
        data,
        isDraggable,
        onComponentStartDrag,
        onComponentEndDrag
      );
      this.addCanvasRef(instance, instance.id);
      return instance;
    }
  }

  savePageMetadata(appId, pageName, refeshCanvas) {
    const pageMetadata = this.canvasElementList[pageName].getMetadata();
    if (pageMetadata && pageMetadata.display) {
      pageMetadata.display = this.fixJson(pageMetadata.display);
    }
    expressionBuilder.updatePageMetadata(appId, pageName, pageMetadata);
    if (refeshCanvas) {
      this.triggerMovedComponent();
    }
  }

  triggerCanvasUpdate(isMove) {
    store.dispatch(MUTATION_NAME.SET_UPDATE_DRAGGABLE, {
      refeshCanvas: isMove
    });
  }
  triggerMovedComponent() {
    store.dispatch(MUTATION_NAME.SET_MOVE_DRAGGABLE, {});
  }

  /**
   *
   * @param {Objcet} data - Json object with info to create a component
   */
  setCreateElementData(data) {
    this.createElementData = data;
  }

  /**
   * Returns Json object with info to create a component
   */
  getCreateElementData() {
    return this.createElementData;
  }

  /**
   * Creates an instance of modal to add a new component
   * @param {String} from - name of the modal
   * @param {Funciton} dismissModal - Function callback to dismiss the modal
   * @param {Function} onModalDone - Function callcack to create component
   * @param {Object} editData - Optional object with data to edit an existing component
   */
  getModalInstance(from, dismissModal, onModalDone, editData) {
    const data = {
      props: {
        show: true,
        maxHeight: 400
      },
      events: {
        "on-add-modal-close": dismissModal,
        "on-add-modal-done": onModalDone
      }
    };
    if (editData) {
      data.props.editData = editData;
    }
    return this.buildIntance(from, data);
  }

  /**
   * Adds instance to canvasElementList
   * @param {Vue} instance
   * @param {String} id
   */
  addCanvasRef(instance, id) {
    this.canvasElementList[id] = instance;
  }

  /**
   * Creates a draggable instance of a component
   * @param {String} from
   * @param {Object} data - Json object with props of the component
   * @param {Boolean} isDraggable - Optional boolean defaults to true
   * @param {Function} startDragCallback - Optional function callback external to current component
   * @param {Function} endDragCallback - Optional function callback external to current component
   */
  buildDraggableInstance(from, data, isDraggable, startDragCallback, endDragCallback) {
    const component = this.buildIntance(from, data);
    // proxied methods
    component.onComponentDragEnter = ProxyElements.onComponentDragEnter;
    component.onComponentStartDrag = ProxyElements.onComponentStartDrag;
    component.onComponentEndDrag = ProxyElements.onComponentEndDrag;
    component.onCanvasDropzone = ProxyElements.onCanvasDropzone;
    component.onComponentDrop = ProxyElements.onComponentDrop;
    component.updateComponent = ProxyElements.updateComponent;
    component.onElementEdit = ProxyElements.onElementEdit;
    component.onDeleteComponent = ProxyElements.onDeleteComponent;
    component.showEditModal = ProxyElements.showEditModal;
    component.removeModal = ProxyElements.removeModal;
    component.onModalCreate = ProxyElements.onModalCreate;
    component.removeDropzoneLines = ProxyElements.removeDropzoneLines;

    component.draggable = isDraggable == null ? true : Boolean(isDraggable);

    // optional callbacks outside its own container. E.g. a canvas layout
    if (startDragCallback) {
      component.$on("drag-component-start", startDragCallback);
    }
    if (endDragCallback) {
      component.$on("drag-component-end", endDragCallback);
    }
    return component;
  }

  /**
   * Creates a instance of a component
   * @param {String} from - Name of the compnent
   * @param {Object} data - Json object with props of the component
   */
  buildIntance(from, data) {
    const ComponentClass = Vue.extend(from);
    const instance = new ComponentClass({
      i18n,
      store,
      router
    });

    data.props = data.props || {};
    data.events = data.events || {};
    for (const [key, value] of Object.entries(data.props)) {
      instance[key] = value;
    }
    if (!instance.id) {
      instance.id =
        data.props && data.props.id
          ? data.props.id
          : this.generateId(from.name);
    }
    for (const [key, value] of Object.entries(data.events)) {
      instance.$on(key, value);
    }
    instance.$mount();
    return instance;
  }

  /**
   * Generates a unique id when one is not provided.
   * @param {String} name - String to be prepended to the final id
   */
  generateId(name) {
    return name + "-" + uuidv4();
  }

  /**
   * Function to get normalized name of the component. E.g. my-component-name will be MyComponentName
   * @param {String} name
   */
  getBuilderName(name) {
    return name
      .replace(/\b[a-z]/g, st => {
        return st.toUpperCase();
      })
      .replace(/-/g, "");
  }

  componentStartDragging(instance) {
    for (const [, component] of Object.entries(this.canvasElementList)) {
      if (component instanceof Vue && component.onChildComponentStartDragging) {
        component.onChildComponentStartDragging(instance);
      }
    }
  }

  componentEndDragging(instance) {
    for (const [, component] of Object.entries(this.canvasElementList)) {
      if (component instanceof Vue && component.onChildComponentEndDragging) {
        component.onChildComponentEndDragging(instance);
      }
    }
  }

  componentDropzone(ev, refConponent) {
    for (const [, component] of Object.entries(this.canvasElementList)) {
      if (component instanceof Vue && component.onComponentDropzone) {
        component.onComponentDropzone(ev, refConponent);
      }
    }
  }
  deleteComponent(ev) {
    for (const [, component] of Object.entries(this.canvasElementList)) {
      if (component instanceof Vue && component.onDeleteComponent) {
        if (component.onDeleteComponent(ev.detail)) {
          // found, no need to continue
          break;
        }
      }
    }
  }

  deserializeComponent(serializedJson, appId) {
    if (!serializedJson.element.name) {
      // will remove this block later
      const type = serializedJson.element;
      serializedJson.element = {
        name: serializedJson.element
      };
      if (type === "card-layout") {
        serializedJson.element.card = true;
      }
    }
    const from = this.getBuilderName(serializedJson.element.name);
    const instance = this.createDraggableComponent(from, {}, serializedJson.element.card);
    if (typeof instance.deserialize === "function") {
      instance.deserialize(serializedJson, appId);
      const content = {
        //type: serializedJson.type,
        body: serializedJson.body,
        children: serializedJson.children
      };
      const deserializedInstance = {
        instance,
        content
      };
      return deserializedInstance;
    }
  }

  fixJson(json) {
    if (json) {
      if (json.type === LAYOUT_TYPE.ROW || json.type === LAYOUT_TYPE.COLUMN) {
        for (const i in json.children) {
          const fixed = this.fixJson(json.children[i]);
          json.children[i] = fixed;
        }
      }
      if (!json.element.card && json.type === LAYOUT_TYPE.SECTION) {
        const fixed = this.fixJson(json.body);
        if (fixed) {
          json = fixed;
        }
      }
      if (!json.element.card && json.body && json.body.element.card) {
        json = json.body;
      }
    }
    return json;
  }
}

export default new DragAndDrop();
